import Address from '../../models/Address';
import AddressMapResult, { CustomMapBounds } from '../../models/AddressMapResult';
import { asFloat } from '../../services/ValueChecker';

export const DEFAULT_MAP_CENTER: google.maps.LatLngLiteral = { lat: -37.8136, lng: 144.9631 };
export const MARKER_LABEL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const MAX_NEARBY_DISTANCE_KM = 2;

export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const earthRadiusKm = 6371;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

export function getAddressIdsKey(addresses: Address[] | AddressMapResult[]): string {
  return JSON.stringify(addresses.map((address) => address.id || address.placeId));
}

export function getInitialMapCenter(
  addresses: Address[] | AddressMapResult[],
): google.maps.LatLngLiteral {
  if (addresses.length === 0) {
    return DEFAULT_MAP_CENTER;
  }

  return {
    lat: asFloat(addresses[0].latitude),
    lng: asFloat(addresses[0].longitude),
  };
}

export function getMarkerLabel(address: Address | AddressMapResult, index: number): string {
  const nearbyCount = (address as AddressMapResult).nearbyPlaces?.length ?? 0;
  if (nearbyCount > 0) {
    return '  ';
  }

  return String.fromCharCode(65 + (index % 26)) + (index >= 26 ? Math.floor(index / 26) : '');
}

export function hasNearbyPlaces(addresses: Address[] | AddressMapResult[]): boolean {
  return addresses.some(
    (address) =>
      'nearbyPlaces' in address &&
      Array.isArray((address as AddressMapResult).nearbyPlaces) &&
      (address as AddressMapResult).nearbyPlaces!.length > 0,
  );
}

export function isContainerMeasurable(element: HTMLElement | null): boolean {
  if (!element || !element.isConnected) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function shouldFitBounds(
  nearbyPlacesPresent: boolean,
  diagonalKm: number,
  addressCount: number,
): boolean {
  return nearbyPlacesPresent || diagonalKm > 0.3 || addressCount > 1;
}

export function extendBoundsForAddresses(
  bounds: google.maps.LatLngBounds,
  addresses: Address[] | AddressMapResult[],
  maxNearbyDistanceKm = MAX_NEARBY_DISTANCE_KM,
): { diagonalKm: number; nearbyPlacesPresent: boolean } {
  addresses.forEach((address) => {
    const lat = asFloat(address.latitude);
    const lng = asFloat(address.longitude);
    bounds.extend({ lat, lng });

    if ('nearbyPlaces' in address && Array.isArray(address.nearbyPlaces)) {
      address.nearbyPlaces.forEach((place) => {
        const placeLat = asFloat(place.coordinates.latitude);
        const placeLng = asFloat(place.coordinates.longitude);
        const distanceKm = haversineDistance(lat, lng, placeLat, placeLng);
        if (distanceKm <= maxNearbyDistanceKm) {
          bounds.extend({ lat: placeLat, lng: placeLng });
        }
      });
    }
  });

  const northEast = bounds.getNorthEast();
  const southWest = bounds.getSouthWest();
  const diagonalKm =
    northEast && southWest
      ? haversineDistance(southWest.lat(), southWest.lng(), northEast.lat(), northEast.lng())
      : 0;

  return {
    diagonalKm,
    nearbyPlacesPresent: hasNearbyPlaces(addresses),
  };
}

export function buildStaticMapUrl(
  addresses: Address[] | AddressMapResult[],
  options: {
    printMode: boolean;
    mapBounds?: CustomMapBounds;
    zoomOnSingle: number;
  },
): string | null {
  const { printMode, mapBounds, zoomOnSingle } = options;

  if (!printMode || addresses.length === 0) {
    return null;
  }

  const primaryAddresses = addresses.filter(
    (address) =>
      address.latitude !== undefined &&
      address.latitude !== null &&
      address.longitude !== undefined &&
      address.longitude !== null,
  );

  if (primaryAddresses.length === 0) {
    return null;
  }

  const params = new URLSearchParams();
  params.set('size', '640x360');
  params.set('scale', '2');
  params.set('maptype', 'roadmap');

  primaryAddresses.forEach((address, index) => {
    const lat = asFloat(address.latitude);
    const lng = asFloat(address.longitude);
    const label = MARKER_LABEL_ALPHABET[index % MARKER_LABEL_ALPHABET.length] || 'A';
    params.append('markers', `color:red|label:${label}|${lat},${lng}`);
    params.append('visible', `${lat},${lng}`);
  });

  if (mapBounds) {
    const neLat = asFloat(mapBounds.northEast.latitude);
    const neLng = asFloat(mapBounds.northEast.longitude);
    const swLat = asFloat(mapBounds.southWest.latitude);
    const swLng = asFloat(mapBounds.southWest.longitude);
    params.append('visible', `${neLat},${neLng}`);
    params.append('visible', `${swLat},${swLng}`);
  }

  if (primaryAddresses.length === 1) {
    const lat = asFloat(primaryAddresses[0].latitude);
    const lng = asFloat(primaryAddresses[0].longitude);
    params.set('center', `${lat},${lng}`);
    params.set('zoom', String(zoomOnSingle));
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}

export function resetMapView(
  map: google.maps.Map,
  addresses: Address[] | AddressMapResult[],
  zoomOnSingle: number,
): void {
  if (addresses.length === 1) {
    const center = {
      lat: asFloat(addresses[0].latitude),
      lng: asFloat(addresses[0].longitude),
    };
    map.setCenter(center);
    map.setZoom(zoomOnSingle);
    return;
  }

  if (addresses.length > 1) {
    const bounds = new window.google.maps.LatLngBounds();
    addresses.forEach(({ latitude, longitude }) =>
      bounds.extend({ lat: asFloat(latitude), lng: asFloat(longitude) }),
    );
    map.fitBounds(bounds);
  }
}
